import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft, Activity, AlertTriangle, Search, Eye, X, ChevronLeft, ChevronRight, RotateCcw } from 'lucide-react';
import Sidebar from './Sidebar';
import UserDropdown from './UserDropdown';
import adminApi from '../api/adminApi';

const PAGE_SIZE = 20;

// DB timestamps arrive as "YYYY-MM-DD HH:MM:SS" strings; reformat without
// going through Date so no timezone shift can creep in.
const formatDateTime = (value, withSeconds = false) => {
  if (!value) return '-';
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/.exec(value);
  if (!m) return value;
  return `${m[3]}-${m[2]}-${m[1]} ${m[4]}:${m[5]}${withSeconds ? `:${m[6]}` : ''}`;
};

const describeUserAgent = (ua) => {
  if (!ua) return '-';
  const browser = /Edg\//.test(ua) ? 'Edge'
    : /OPR\//.test(ua) ? 'Opera'
      : /Chrome\//.test(ua) ? 'Chrome'
        : /Firefox\//.test(ua) ? 'Firefox'
          : /Safari\//.test(ua) ? 'Safari'
            : /node|curl|postman/i.test(ua) ? 'API client' : 'Unknown browser';
  const os = /Windows/.test(ua) ? 'Windows'
    : /Android/.test(ua) ? 'Android'
      : /iPhone|iPad/.test(ua) ? 'iOS'
        : /Mac OS X/.test(ua) ? 'macOS'
          : /Linux/.test(ua) ? 'Linux' : null;
  return os ? `${browser} / ${os}` : browser;
};

const LEVEL_STYLES = {
  INFO: { bg: '#eff6ff', color: '#1d4ed8' },
  WARNING: { bg: '#fef9c3', color: '#a16207' },
  ERROR: { bg: '#fee2e2', color: '#b91c1c' },
  CRITICAL: { bg: '#7f1d1d', color: '#ffffff' }
};

const Badge = ({ bg, color, children }) => (
  <span style={{ padding: '0.2rem 0.5rem', borderRadius: '0.25rem', fontSize: '0.7rem', fontWeight: 600, backgroundColor: bg, color, whiteSpace: 'nowrap' }}>
    {children}
  </span>
);

const StatusBadge = ({ status }) => (status === 'success'
  ? <Badge bg="#dcfce7" color="#15803d">Success</Badge>
  : <Badge bg="#fee2e2" color="#b91c1c">Failed</Badge>);

const LevelBadge = ({ level }) => {
  const s = LEVEL_STYLES[level] || LEVEL_STYLES.INFO;
  return <Badge bg={s.bg} color={s.color}>{level}</Badge>;
};

const inputStyle = {
  padding: '0.5rem 0.625rem',
  border: '1px solid #d1d5db',
  borderRadius: '0.5rem',
  fontSize: '0.8125rem',
  backgroundColor: 'white',
  color: '#1f2937',
  minWidth: 0
};
const thStyle = { padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.8125rem', fontWeight: 500, color: '#374151', whiteSpace: 'nowrap' };
const tdStyle = { padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#1f2937', verticalAlign: 'top' };

const FilterField = ({ label, children }) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.75rem', color: '#6b7280', fontWeight: 500 }}>
    {label}
    {children}
  </label>
);

const Select = ({ value, onChange, options, placeholder }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} style={inputStyle}>
    <option value="">{placeholder}</option>
    {options.map((o) => (typeof o === 'object'
      ? <option key={o.value} value={o.value}>{o.label}</option>
      : <option key={o} value={o}>{o}</option>))}
  </select>
);

/**
 * Shared list state for both log tabs: filters, debounced search,
 * pagination, loading and error handling.
 */
function useLogList(fetchPage, initialFilters) {
  const [filters, setFilters] = useState(initialFilters);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const requestSeq = useRef(0);

  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(id);
  }, [search]);

  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError('');
    const params = { page, limit: PAGE_SIZE, search: debouncedSearch || undefined };
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    try {
      const response = await fetchPage(params);
      if (seq !== requestSeq.current) return; // a newer request superseded this one
      setRows(response.data.data);
      setPagination(response.data.pagination);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setRows([]);
      setError(err.response?.data?.message || 'Failed to load logs');
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [fetchPage, filters, page, debouncedSearch]);

  useEffect(() => { load(); }, [load]);

  const setFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };
  const onSearch = (value) => {
    setSearch(value);
    setPage(1);
  };
  const reset = () => {
    setFilters(initialFilters);
    setSearch('');
    setPage(1);
  };

  return { filters, setFilter, search, onSearch, page, setPage, rows, pagination, loading, error, reload: load, reset };
}

const Pagination = ({ page, pagination, onChange }) => {
  if (!pagination) return null;
  const { total_pages: totalPages, total_items: totalItems } = pagination;
  const btn = (disabled) => ({
    display: 'flex', alignItems: 'center', padding: '0.4rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '0.375rem',
    backgroundColor: disabled ? '#f9fafb' : 'white', color: disabled ? '#d1d5db' : '#374151', cursor: disabled ? 'not-allowed' : 'pointer'
  });
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 1rem', borderTop: '1px solid #e5e7eb', flexWrap: 'wrap', gap: '0.5rem' }}>
      <span style={{ fontSize: '0.8125rem', color: '#6b7280' }}>
        {totalItems} {totalItems === 1 ? 'entry' : 'entries'} · Page {page} of {totalPages}
      </span>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button aria-label="Previous page" disabled={page <= 1} onClick={() => onChange(page - 1)} style={btn(page <= 1)}><ChevronLeft size={16} /></button>
        <button aria-label="Next page" disabled={page >= totalPages} onClick={() => onChange(page + 1)} style={btn(page >= totalPages)}><ChevronRight size={16} /></button>
      </div>
    </div>
  );
};

const TableShell = ({ columns, loading, error, onRetry, empty, children, footer }) => (
  <div style={{ backgroundColor: 'white', borderRadius: '0.75rem', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
    <div className="crm-sticky-table">
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '820px' }}>
        <thead>
          <tr style={{ backgroundColor: '#f9fafb' }}>
            {columns.map((c) => <th key={c} style={thStyle}>{c}</th>)}
            <th style={{ ...thStyle, textAlign: 'right' }}>Details</th>
          </tr>
        </thead>
        <tbody style={{ opacity: loading ? 0.5 : 1, transition: 'opacity 0.15s' }}>
          {error ? (
            <tr><td colSpan={columns.length + 1} style={{ ...tdStyle, textAlign: 'center', padding: '2rem', color: '#dc2626' }}>
              {error} <button onClick={onRetry} style={{ marginLeft: '0.5rem', color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500 }}>Retry</button>
            </td></tr>
          ) : empty ? (
            <tr><td colSpan={columns.length + 1} style={{ ...tdStyle, textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
              {loading ? 'Loading...' : 'No log entries match these filters.'}
            </td></tr>
          ) : children}
        </tbody>
      </table>
    </div>
    {footer}
  </div>
);

const ViewButton = ({ onClick }) => (
  <button onClick={onClick} aria-label="View details" title="View details"
    style={{ padding: '0.4rem', backgroundColor: '#f3f4f6', border: 'none', borderRadius: '0.25rem', cursor: 'pointer', color: '#374151' }}>
    <Eye size={15} />
  </button>
);

const FiltersBar = ({ search, onSearch, searchPlaceholder, onReset, children }) => (
  <div style={{ backgroundColor: 'white', borderRadius: '0.75rem', border: '1px solid #e5e7eb', padding: '1rem', marginBottom: '1rem' }}>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '0.75rem', alignItems: 'end' }}>
      <FilterField label="Search">
        <div style={{ position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: '0.55rem', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
          <input value={search} onChange={(e) => onSearch(e.target.value)} placeholder={searchPlaceholder} style={{ ...inputStyle, width: '100%', paddingLeft: '1.8rem' }} />
        </div>
      </FilterField>
      {children}
      <button onClick={onReset} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem', padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '0.5rem', backgroundColor: 'white', color: '#374151', fontSize: '0.8125rem', cursor: 'pointer' }}>
        <RotateCcw size={14} /> Reset
      </button>
    </div>
  </div>
);

const DateRange = ({ filters, setFilter }) => (
  <>
    <FilterField label="From">
      <input type="date" value={filters.date_from} max={filters.date_to || undefined} onChange={(e) => setFilter('date_from', e.target.value)} style={inputStyle} />
    </FilterField>
    <FilterField label="To">
      <input type="date" value={filters.date_to} min={filters.date_from || undefined} onChange={(e) => setFilter('date_to', e.target.value)} style={inputStyle} />
    </FilterField>
  </>
);

function useFilterOptions(fetchOptions) {
  const [options, setOptions] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetchOptions()
      .then((res) => { if (!cancelled) setOptions(res.data.data); })
      .catch(() => { if (!cancelled) setOptions({}); });
    return () => { cancelled = true; };
  }, [fetchOptions]);
  return options || {};
}

// ---------------------------------------------------------------------------
// Detail modal
// ---------------------------------------------------------------------------

const DetailRow = ({ label, children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0.75rem', padding: '0.5rem 0', borderBottom: '1px solid #f3f4f6', fontSize: '0.8125rem' }}>
    <div style={{ color: '#6b7280', fontWeight: 500 }}>{label}</div>
    <div style={{ color: '#1f2937', wordBreak: 'break-word' }}>{children ?? '-'}</div>
  </div>
);

const JsonBlock = ({ label, value }) => {
  if (value === null || value === undefined || (typeof value === 'object' && !Object.keys(value).length)) return null;
  return (
    <div style={{ marginTop: '1rem' }}>
      <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#374151', marginBottom: '0.35rem' }}>{label}</div>
      <pre style={{ margin: 0, padding: '0.75rem', backgroundColor: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: '0.5rem', fontSize: '0.75rem', overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '240px' }}>
        {typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
};

const DetailModal = ({ title, loading, onClose, children }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    // zIndex above Sidebar's 1000/1001, which would otherwise sit on top of the modal.
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '1rem' }}>
      <div role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}
        style={{ backgroundColor: 'white', borderRadius: '0.75rem', width: '100%', maxWidth: '720px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.5rem', borderBottom: '1px solid #e5e7eb' }}>
          <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: '#1f2937', margin: 0 }}>{title}</h3>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', display: 'flex' }}><X size={20} /></button>
        </div>
        <div style={{ padding: '1rem 1.5rem 1.5rem', overflowY: 'auto' }}>
          {loading ? <p style={{ color: '#6b7280' }}>Loading...</p> : children}
        </div>
      </div>
    </div>
  );
};

function useDetail(fetchOne) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const show = async (id) => {
    setOpen(true);
    setLoading(true);
    setData(null);
    try {
      const res = await fetchOne(id);
      setData(res.data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load log details');
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };
  const close = useCallback(() => setOpen(false), []);
  return { open, loading, data, show, close };
}

// ---------------------------------------------------------------------------
// User action logs
// ---------------------------------------------------------------------------

const USER_ACTION_FILTERS = { date_from: '', date_to: '', user_id: '', module: '', action: '', status: '' };

const UserActionLogs = () => {
  const list = useLogList(adminApi.listUserActionLogs, USER_ACTION_FILTERS);
  const options = useFilterOptions(adminApi.getUserActionLogFilters);
  const detail = useDetail(adminApi.getUserActionLog);
  const { filters, setFilter } = list;
  const d = detail.data;

  return (
    <>
      <FiltersBar search={list.search} onSearch={list.onSearch} searchPlaceholder="Action, user, IP, request ID" onReset={list.reset}>
        <DateRange filters={filters} setFilter={setFilter} />
        <FilterField label="User">
          <Select value={filters.user_id} onChange={(v) => setFilter('user_id', v)} placeholder="All users"
            options={(options.users || []).map((u) => ({ value: String(u.id), label: u.username }))} />
        </FilterField>
        <FilterField label="Module">
          <Select value={filters.module} onChange={(v) => setFilter('module', v)} placeholder="All modules" options={options.modules || []} />
        </FilterField>
        <FilterField label="Action">
          <Select value={filters.action} onChange={(v) => setFilter('action', v)} placeholder="All actions" options={options.actions || []} />
        </FilterField>
        <FilterField label="Status">
          <Select value={filters.status} onChange={(v) => setFilter('status', v)} placeholder="All statuses"
            options={[{ value: 'success', label: 'Success' }, { value: 'failed', label: 'Failed' }]} />
        </FilterField>
      </FiltersBar>

      <TableShell
        columns={['Date/Time', 'User', 'Module', 'Action', 'Status', 'IP Address']}
        loading={list.loading}
        error={list.error}
        onRetry={list.reload}
        empty={!list.rows.length}
        footer={<Pagination page={list.page} pagination={list.pagination} onChange={list.setPage} />}
      >
        {list.rows.map((row) => (
          <tr key={row.id} style={{ borderTop: '1px solid #e5e7eb' }}>
            <td style={{ ...tdStyle, whiteSpace: 'nowrap', color: '#6b7280' }}>{formatDateTime(row.created_at)}</td>
            <td style={tdStyle}>{row.user_name || <span style={{ color: '#9ca3af' }}>Unknown</span>}</td>
            <td style={tdStyle}>{row.module}</td>
            <td style={tdStyle}>
              <div style={{ fontWeight: 500 }}>{row.action}</div>
              {row.description && <div style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.15rem', maxWidth: '360px' }}>{row.description}</div>}
            </td>
            <td style={tdStyle}><StatusBadge status={row.status} /></td>
            <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.75rem' }}>{row.ip_address || '-'}</td>
            <td style={{ ...tdStyle, textAlign: 'right' }}><ViewButton onClick={() => detail.show(row.id)} /></td>
          </tr>
        ))}
      </TableShell>

      {detail.open && (
        <DetailModal title="Action Details" loading={detail.loading} onClose={detail.close}>
          {d && (
            <>
              <DetailRow label="User">{d.user_name || d.username || 'Unknown'}{d.user_email ? ` (${d.user_email})` : ''}</DetailRow>
              <DetailRow label="Action">{d.action}</DetailRow>
              <DetailRow label="Module">{d.module}</DetailRow>
              <DetailRow label="Status"><StatusBadge status={d.status} /></DetailRow>
              <DetailRow label="Description">{d.description}</DetailRow>
              <DetailRow label="IP Address">{d.ip_address}</DetailRow>
              <DetailRow label="User Agent">
                {describeUserAgent(d.user_agent)}
                {d.user_agent && <div style={{ color: '#9ca3af', fontSize: '0.7rem', marginTop: '0.2rem' }}>{d.user_agent}</div>}
              </DetailRow>
              <DetailRow label="Request Method">{d.request_method}</DetailRow>
              <DetailRow label="Request URL"><code style={{ fontSize: '0.75rem' }}>{d.request_url}</code></DetailRow>
              <DetailRow label="Request ID"><code style={{ fontSize: '0.75rem' }}>{d.request_id}</code></DetailRow>
              <DetailRow label="Date">{formatDateTime(d.created_at, true)}</DetailRow>
              <JsonBlock label="Previous Values" value={d.old_values} />
              <JsonBlock label="New Values" value={d.new_values} />
              <JsonBlock label="Metadata" value={d.metadata} />
            </>
          )}
        </DetailModal>
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// Error logs
// ---------------------------------------------------------------------------

const ERROR_FILTERS = { date_from: '', date_to: '', level: '', module: '', user_id: '', request_id: '' };

const ErrorLogs = ({ initialRequestId }) => {
  const list = useLogList(adminApi.listErrorLogs, { ...ERROR_FILTERS, request_id: initialRequestId || '' });
  const options = useFilterOptions(adminApi.getErrorLogFilters);
  const detail = useDetail(adminApi.getErrorLog);
  const { filters, setFilter } = list;
  const d = detail.data;

  return (
    <>
      <FiltersBar search={list.search} onSearch={list.onSearch} searchPlaceholder="Message, exception, URL" onReset={list.reset}>
        <DateRange filters={filters} setFilter={setFilter} />
        <FilterField label="Level">
          <Select value={filters.level} onChange={(v) => setFilter('level', v)} placeholder="All levels" options={options.levels || ['INFO', 'WARNING', 'ERROR', 'CRITICAL']} />
        </FilterField>
        <FilterField label="Module">
          <Select value={filters.module} onChange={(v) => setFilter('module', v)} placeholder="All modules" options={options.modules || []} />
        </FilterField>
        <FilterField label="User">
          <Select value={filters.user_id} onChange={(v) => setFilter('user_id', v)} placeholder="All users"
            options={(options.users || []).map((u) => ({ value: String(u.id), label: u.username }))} />
        </FilterField>
        <FilterField label="Request ID">
          <input value={filters.request_id} onChange={(e) => setFilter('request_id', e.target.value.trim())} placeholder="REQ-..." style={inputStyle} />
        </FilterField>
      </FiltersBar>

      <TableShell
        columns={['Date/Time', 'Level', 'Module', 'Message', 'User', 'Request ID']}
        loading={list.loading}
        error={list.error}
        onRetry={list.reload}
        empty={!list.rows.length}
        footer={<Pagination page={list.page} pagination={list.pagination} onChange={list.setPage} />}
      >
        {list.rows.map((row) => (
          <tr key={row.id} style={{ borderTop: '1px solid #e5e7eb' }}>
            <td style={{ ...tdStyle, whiteSpace: 'nowrap', color: '#6b7280' }}>{formatDateTime(row.created_at)}</td>
            <td style={tdStyle}><LevelBadge level={row.level} /></td>
            <td style={tdStyle}>{row.module}</td>
            <td style={{ ...tdStyle, maxWidth: '380px' }}>
              <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{row.message}</div>
              {row.request_url && <div style={{ color: '#9ca3af', fontSize: '0.7rem', marginTop: '0.15rem', fontFamily: 'monospace' }}>{row.request_method} {row.request_url}</div>}
            </td>
            <td style={tdStyle}>{row.user_name || (row.user_id ? `#${row.user_id}` : '-')}</td>
            <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{row.request_id || '-'}</td>
            <td style={{ ...tdStyle, textAlign: 'right' }}><ViewButton onClick={() => detail.show(row.id)} /></td>
          </tr>
        ))}
      </TableShell>

      {detail.open && (
        <DetailModal title="Error Details" loading={detail.loading} onClose={detail.close}>
          {d && (
            <>
              <DetailRow label="Level"><LevelBadge level={d.level} /></DetailRow>
              <DetailRow label="Module">{d.module}</DetailRow>
              <DetailRow label="Message">{d.message}</DetailRow>
              <DetailRow label="Exception">{d.exception}</DetailRow>
              <DetailRow label="Request ID"><code style={{ fontSize: '0.75rem' }}>{d.request_id}</code></DetailRow>
              <DetailRow label="User">{d.user_name || (d.user_id ? `#${d.user_id}` : null)}</DetailRow>
              <DetailRow label="IP Address">{d.ip_address}</DetailRow>
              <DetailRow label="User Agent">{describeUserAgent(d.user_agent)}</DetailRow>
              <DetailRow label="Request">{d.request_method ? <code style={{ fontSize: '0.75rem' }}>{d.request_method} {d.request_url}</code> : null}</DetailRow>
              <DetailRow label="Date">{formatDateTime(d.created_at, true)}</DetailRow>
              <JsonBlock label="Metadata" value={d.metadata} />
              <JsonBlock label="Request Payload (sensitive values masked)" value={d.payload} />
              <JsonBlock label="Stack Trace" value={d.stack_trace} />
            </>
          )}
        </DetailModal>
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const TABS = [
  { key: 'user-actions', label: 'User Action Logs', icon: Activity, description: 'Audit trail of sign-ins, security events and changes made across the CRM.' },
  { key: 'errors', label: 'Error Logs', icon: AlertTriangle, description: 'Application errors captured by the server. Search by the request ID a user reports.' }
];

const Logs = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const active = TABS.find((t) => t.key === searchParams.get('tab')) || TABS[0];

  return (
    <div className="main-container" style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      <Sidebar />

      <div className="main-content" style={{ flex: 1, padding: '2rem', minWidth: 0 }}>
        <div className="header-section" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
              <button onClick={() => navigate('/dashboard')} aria-label="Back to dashboard"
                style={{ display: 'flex', alignItems: 'center', padding: '0.5rem', border: 'none', backgroundColor: 'transparent', color: '#6b7280', cursor: 'pointer', marginRight: '1rem' }}>
                <ArrowLeft size={20} />
              </button>
              <h2 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1f2937' }}>Logs</h2>
            </div>
            <p style={{ color: '#6b7280' }}>{active.description}</p>
          </div>
          <div className="user-dropdown">
            <UserDropdown />
          </div>
        </div>

        <div role="tablist" style={{ display: 'flex', gap: '0.25rem', borderBottom: '1px solid #e5e7eb', marginBottom: '1.5rem', overflowX: 'auto' }}>
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = tab.key === active.key;
            return (
              <button key={tab.key} role="tab" aria-selected={isActive} onClick={() => setSearchParams({ tab: tab.key })}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1rem', border: 'none',
                  borderBottom: `2px solid ${isActive ? '#3b82f6' : 'transparent'}`, marginBottom: '-1px', backgroundColor: 'transparent',
                  color: isActive ? '#1d4ed8' : '#6b7280', fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap'
                }}>
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {active.key === 'errors'
          ? <ErrorLogs key="errors" initialRequestId={searchParams.get('request_id')} />
          : <UserActionLogs key="user-actions" />}
      </div>
    </div>
  );
};

export default Logs;
